import React, { useState, useEffect, useRef } from "react";
import { Spin, Alert } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import ContinueButton from "../../../assets/AntDesign/Buttons/ContinueButton";
import { loginDoctor, getCurrentDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./LoginDoctor.css";
import "../../../authPatient/pages/AuthLayout.css";
import Cookies from "js-cookie";
import { useTheme } from "../../../places/context/ThemeContext.jsx";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../Landing/components/LanguageSelector/LanguageSelector.jsx";
import {
  selectDoctorLoading,
  selectDoctorError,
} from "../../../authDoctor/redux/selectors/doctorSelectors";
import { selectDoctorUser } from "../../../authDoctor/redux/selectors/doctorsSelectors";
import {
  clearError,
  restoreSessionFromStorage,
} from "../../redux/slices/doctorsSlice.js";
import ROUTES from "../../../routes/patientRoutes/routePaths.js";
import { CABINET_ROUTES } from "../../../cabinetDashboard/routePaths.js";
import { getDoctorRedirectTarget } from "../../functions/getDoctorRedirectTarget.js";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";

/** Input field with toggle password visibility */
const FormInput = ({
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  error,
  name,
  autoComplete,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const actualType = isPassword && showPassword ? "text" : type;

  return (
    <div className="ld-form-item">
      {label && <label htmlFor={name}>{label}</label>}
      <div className="ld-input-wrapper">
        <input
          id={name}
          name={name}
          type={actualType}
          placeholder={placeholder}
          value={value ?? ""}
          onChange={onChange}
          className={error ? "error" : ""}
          autoComplete={autoComplete}
        />
        {isPassword && (
          <button
            type="button"
            className="ld-password-toggle"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeInvisibleOutlined className="ld-eye-icon" />
            ) : (
              <EyeOutlined className="ld-eye-icon" />
            )}
          </button>
        )}
      </div>
      {error ? <span className="ld-error-message">{error}</span> : null}
    </div>
  );
};

export default function LoginDoctor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const loading = useSelector(selectDoctorLoading);
  const error = useSelector(selectDoctorError);
  const doctorUser = useSelector(selectDoctorUser);
  const [sessionReady, setSessionReady] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (theme !== "light") setTheme("light");
  }, [theme, setTheme]);

  useEffect(() => {
    dispatch(restoreSessionFromStorage());
    const timer = window.setTimeout(() => setSessionReady(true), 0);
    return () => window.clearTimeout(timer);
  }, [dispatch]);

  const hasDoctorSession = Boolean(
    doctorUser?.id ||
      doctorUser?._id ||
      (typeof window !== "undefined" &&
        (Cookies.get("doctorAccessToken") ||
          Cookies.get("doctorToken") ||
          localStorage.getItem("doctorAccessToken") ||
          localStorage.getItem("doctorToken")) &&
        (doctorUser?.doctorId ||
          doctorUser?.medecinId ||
          Cookies.get("doctor_id") ||
          Cookies.get("doctorId") ||
          localStorage.getItem("userId")))
  );
  const redirectTarget = getDoctorRedirectTarget(location.state);

  useEffect(() => {
    if (!sessionReady || !hasDoctorSession) return;
    setIsRedirecting(true);
    navigate(redirectTarget, { replace: true });
  }, [sessionReady, hasDoctorSession, navigate, redirectTarget]);

  // ── Pré-remplissage après création de compte (email + mot de passe) ──
  const [form, setForm] = useState(() => ({
    email: location.state?.email || "",
    password: location.state?.password || "",
  }));
  const isValid = !!(form.email && form.password);

  // Efface le mot de passe de l'historique du navigateur une fois lu
  useEffect(() => {
    if (location.state?.email || location.state?.password) {
      const state = {};
      if (typeof location.state?.heroOnLeft === "boolean") {
        state.heroOnLeft = location.state.heroOnLeft;
      }
      if (location.state?.redirectTo) state.redirectTo = location.state.redirectTo;
      if (location.state?.from) state.from = location.state.from;
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(state).length ? state : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? true : stored === "true"; // true = layout par défaut (hero gauche, form droite)
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour l'animation du swap des 2 cards ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      // rend le swap permanent : persistance + transmission à la page suivante
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const handleChange = (key, value) => {
    dispatch(clearError());
    setForm((p) => ({ ...p, [key]: value }));
  };

  const handleAfterLogin = async (payload) => {
    if (!payload) return;

    if (payload.codeSent) {
      navigate("/VerifCode", {
        state: { email: payload.email || form.email, role: "doctor", redirectTo: redirectTarget },
        replace: true,
      });
      return;
    }

    if (payload.isVerified) {
      if (payload.accessToken) {
        Cookies.set("doctorAccessToken", String(payload.accessToken));
        localStorage.setItem("doctorAccessToken", String(payload.accessToken));
      }

      localStorage.setItem("userRole", "doctor");

      await dispatch(getCurrentDoctor()).unwrap();

      setIsRedirecting(true);
      if (redirectTarget.split(/[?#]/, 1)[0] === CABINET_ROUTES.DASHBOARD) {
        sessionStorage.setItem("dashboardWelcomeAnimation", "true");
      }
      navigate(redirectTarget, { replace: true });
      return;
    }

    navigate("/VerifCode", {
      state: { email: payload.email || form.email, role: "doctor", redirectTo: redirectTarget },
      replace: true,
    });
  };

  const handleLogin = async () => {
    if (!isValid || loading) return;

    try {
      const payload = await dispatch(loginDoctor(form)).unwrap();
      handleAfterLogin(payload);
    } catch (e) {
      console.error("Login failed: Email or password incorrect ! ", e);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  if (!sessionReady || hasDoctorSession || isRedirecting) {
    return (
      <div
        className="ld-session-redirect"
        role="status"
        aria-live="polite"
        style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}
      >
        <Spin tip={t("commonn.loading", "Chargement…")} />
      </div>
    );
  }

  return (
    <div className="signup-container signup-doctor-auth" onKeyDown={onKeyDown}>
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate(ROUTES.LANDING)}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>

          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>

          <div className="signup-hero-inner">
            <h3 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient?")}
            </h3>
            <p className="signup-hero-sub">
              {t("signup.createAccountHeroText", "This registration is for doctors only. To book appointments and access care, please  create a patient account instead.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>

        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            <div className="signup-form-header">
              {/* ── Titre bicolore : "Sign In" (foncé) + "as a Doctor" (bleu) ── */}
              <h1 className="signup-form-title-main">
                {t("auth.signInPrefix", "Sign In")}{" "}
                <span className="signup-form-title-accent">
                  {t("auth.signInAccentDoctor", "as a Doctor")}
                </span>
              </h1>
              <p className="signup-form-subtitle">{t("auth.signInSubtitle")}</p>
            </div>

            <div className="signup-form-content">
              {loading && (
                <div className="ld-feedback">
                  <Spin tip={t("commonn.loading")} />
                </div>
              )}

              <div className="ld-form-grid">
                <div className="fld full">
                  <FormInput
                    name="email"
                    label={t("commonn.email")}
                    type="email"
                    placeholder={t("commonn.emailPlaceholder")}
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    autoComplete="username"
                  />
                </div>

                <div className="fld full">
                  <FormInput
                    name="password"
                    label={t("commonn.password")}
                    type="password"
                    placeholder={t("commonn.passwordPlaceholder")}
                    value={form.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    autoComplete="current-password"
                  />
                </div>

                <div className="fld full">
                  <div className="ld-row between">
                    <label className="ld-remember">
                      <input id="remember" type="checkbox" />
                      <span>{t("auth.rememberMe")}</span>
                    </label>

                    <button
                      type="button"
                      className="ld-link"
                      onClick={() => navigate("/ForgetPassword")}
                    >
                      {t("auth.forgotPassword")}
                    </button>
                  </div>
                </div>

                {!!error && (
                  <div className="fld full">
                    <Alert
                      className="ld-alert"
                      type="error"
                      message={
                        typeof error === "string" && error.trim()
                          ? error
                          : "Login failed: Email or password incorrect !"
                      }
                      showIcon
                      closable
                      onClose={() => dispatch(clearError())}
                    />
                  </div>
                )}

                <div className="ld-button-container">
                  <ContinueButton
                    disabled={!isValid || loading}
                    onClick={(e) => {
                      if (e?.preventDefault) e.preventDefault();
                      handleLogin();
                    }}
                    type="button"
                  >
                    {t("auth.continue")}
                  </ContinueButton>
                </div>

                <div className="fld full ld-alt">
                  {t("auth.needAccount")}{" "}
                  <button
                    type="button"
                    className="ld-link"
                    onClick={() => navigate("/register1")}
                  >
                    {t("auth.createOne")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}